import { useTranslation } from 'react-i18next';
import { ReactNode, useState } from 'react';
import Modal from 'shared/overlays/Modal';
import LanguageIcon from 'shared/ui/LanguageIcon';

interface MatchedItem {
  key: string;
  source?: string;
  target?: string;
}

interface SourceChangedItem {
  key: string;
  xliffSource?: string;
  currentValue?: string;
  target?: string;
}

interface OutOfBoundsItem {
  key: string;
  source?: string;
  row: number;
  col: number;
}

interface LocaleMismatchItem {
  key: string;
  source?: string;
  target?: string;
  tableLocale?: string;
}

interface XliffPreviewGroup<T> {
  superId: string;
  tableName: string;
  items: T[];
}

interface XliffMissingTable {
  superId: string;
  name?: string;
  count?: number;
}

interface XliffPreviewSummary {
  total: number;
  matched: number;
  sourceChanged: number;
  outOfBounds: number;
  missingTables: number;
  localeMismatch?: number;
  newTables: number;
}

export interface XliffPreview {
  summary: XliffPreviewSummary;
  sourceLanguage: string;
  targetLanguage: string;
  languageMismatch?: boolean;
  matched: XliffPreviewGroup<MatchedItem>[];
  sourceChanged: XliffPreviewGroup<SourceChangedItem>[];
  outOfBounds: XliffPreviewGroup<OutOfBoundsItem>[];
  localeMismatch?: XliffPreviewGroup<LocaleMismatchItem>[];
  missingTables: XliffMissingTable[];
  newTables: XliffMissingTable[];
}

interface StatusIconProps {
  type: string;
}

function StatusIcon({ type }: StatusIconProps) {
  const icons: Record<string, string> = {
    matched: 'fa-circle-check text-success',
    sourceChanged: 'fa-triangle-exclamation text-warning',
    outOfBounds: 'fa-circle-xmark text-danger',
    missingTables: 'fa-trash text-danger',
    newTables: 'fa-circle-info text-info',
    localeMismatch: 'fa-language text-warning',
  };
  return <i className={`fa-solid ${icons[type]} me-2`} />;
}

interface CollapsibleSectionProps {
  type: string;
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: ReactNode;
}

function CollapsibleSection({ type, title, count, defaultOpen, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  if (count === 0) return null;

  return (
    <div className="mb-3">
      <button type="button" className="btn btn-link text-decoration-none p-0 d-flex align-items-center gap-2 w-100 text-start" onClick={() => setOpen(!open)}>
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'} text-muted`} style={{ width: 12 }} />
        <StatusIcon type={type} />
        <span className="fw-semibold">{title}</span>
        <span className="badge bg-secondary ms-auto">{count}</span>
      </button>
      {open && <div className="mt-2 ms-4">{children}</div>}
    </div>
  );
}

interface PreviewTableProps {
  columns: string[];
  rows: ReactNode;
}

function PreviewTable({ columns, rows }: PreviewTableProps) {
  return (
    <div className="table-responsive" style={{ maxHeight: 250, overflowY: 'auto' }}>
      <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.85rem' }}>
        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
          <tr>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

interface TableAccordionProps<T> {
  tableName: string;
  count: number;
  columns: string[];
  renderRow: (item: T) => ReactNode;
  items: T[];
}

function TableAccordion<T>({ tableName, count, columns, renderRow, items }: TableAccordionProps<T>) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-2">
      <button
        type="button"
        className="btn btn-link text-decoration-none p-0 d-flex align-items-center gap-2 text-start"
        onClick={() => setOpen(!open)}
        style={{ fontSize: '0.85rem' }}
      >
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'} text-muted`} style={{ width: 10 }} />
        <span className="fw-semibold">{tableName}</span>
        <span className="text-muted fw-normal">({count})</span>
      </button>
      {open && (
        <div className="mt-1">
          <PreviewTable columns={columns} rows={items.map(renderRow)} />
        </div>
      )}
    </div>
  );
}

interface GroupedPreviewTablesProps<T> {
  groups: XliffPreviewGroup<T>[];
  columns: string[];
  renderRow: (item: T) => ReactNode;
}

function GroupedPreviewTables<T>({ groups, columns, renderRow }: GroupedPreviewTablesProps<T>) {
  return (
    <>
      {groups.map((group) => (
        <TableAccordion
          key={group.superId}
          tableName={group.tableName}
          count={group.items.length}
          columns={columns}
          renderRow={renderRow}
          items={group.items}
        />
      ))}
    </>
  );
}

interface XliffPreviewModalProps {
  show: boolean;
  onClose: () => void;
  preview: XliffPreview | null;
  onConfirm: (options: { skipSourceChanged: boolean }) => void;
  isImporting?: boolean;
}

export default function XliffPreviewModal({ show, onClose, preview, onConfirm, isImporting }: XliffPreviewModalProps) {
  const { t } = useTranslation(['common', 'xliff-preview']);

  const [skipSourceChanged, setSkipSourceChanged] = useState(true);

  if (!preview) return null;

  const { summary, sourceLanguage, targetLanguage, languageMismatch, matched, sourceChanged, outOfBounds, localeMismatch, missingTables, newTables } = preview;

  const willApply = summary.matched + (skipSourceChanged ? 0 : summary.sourceChanged);
  const missingTranslations = missingTables.reduce((sum, table) => sum + (table.count ?? 0), 0);
  const willSkip = summary.outOfBounds + missingTranslations + (summary.localeMismatch ?? 0) + (skipSourceChanged ? summary.sourceChanged : 0);

  const footer = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
        {willApply} {t('xliff-preview:will-apply')} &middot; {willSkip} {t('xliff-preview:will-skip')}
      </div>
      <div className="d-flex gap-2">
        <button type="button" className="button default" onClick={onClose} disabled={isImporting}>
          {t('common:cancel')}
        </button>
        <button type="button" className="button primary bold" disabled={willApply === 0 || isImporting} onClick={() => onConfirm({ skipSourceChanged })}>
          {isImporting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="fa-solid fa-file-import me-2" />}
          {t('xliff-preview:confirm-import')}
        </button>
      </div>
    </div>
  );

  return (
    <Modal titleLabelKey="xliff-preview:title" show={show} onClose={onClose} size="lg" scrollable footer={footer}>
      <div className="mb-3 d-flex align-items-center gap-2 p-2 border rounded" style={{ fontSize: '0.9rem' }}>
        <i className="fa-solid fa-language text-muted" />
        <span className="text-muted">{t('xliff-preview:translating')}</span>
        <span className="d-inline-flex align-items-center gap-1">
          <LanguageIcon language={sourceLanguage.split('-')[0]} />
          {sourceLanguage}
        </span>
        <i className="fa-solid fa-arrow-right text-muted" />
        <span className="d-inline-flex align-items-center gap-1">
          <LanguageIcon language={targetLanguage.split('-')[0]} />
          {targetLanguage}
        </span>
      </div>

      {languageMismatch && (
        <div className="alert alert-warning d-flex align-items-center gap-2 py-2">
          <i className="fa-solid fa-triangle-exclamation" />
          {t('xliff-preview:language-mismatch')}
        </div>
      )}

      <div className="mb-3 p-3 bg-light rounded d-flex gap-4 flex-wrap" style={{ fontSize: '0.9rem' }}>
        <div>
          <strong>{summary.total}</strong> {t('xliff-preview:total-translations')}
        </div>
        <div className="text-success">
          <strong>{summary.matched}</strong> {t('xliff-preview:matched')}
        </div>
        {summary.sourceChanged > 0 && (
          <div className="text-warning">
            <strong>{summary.sourceChanged}</strong> {t('xliff-preview:source-changed')}
          </div>
        )}
        {summary.outOfBounds > 0 && (
          <div className="text-danger">
            <strong>{summary.outOfBounds}</strong> {t('xliff-preview:out-of-bounds')}
          </div>
        )}
        {summary.missingTables > 0 && (
          <div className="text-danger">
            <strong>{summary.missingTables}</strong> {t('xliff-preview:missing-tables', { count: summary.missingTables })} ({missingTranslations}{' '}
            {t('xliff-preview:translations', { count: missingTranslations })})
          </div>
        )}
        {(summary.localeMismatch ?? 0) > 0 && (
          <div className="text-warning">
            <strong>{summary.localeMismatch}</strong> {t('xliff-preview:locale-mismatch')}
          </div>
        )}
      </div>

      <CollapsibleSection type="matched" title={t('xliff-preview:matched-title')} count={summary.matched}>
        <GroupedPreviewTables
          groups={matched}
          columns={[t('xliff-preview:source'), t('xliff-preview:target')]}
          renderRow={(item) => (
            <tr key={item.key}>
              <td>{item.source}</td>
              <td>{item.target || <span className="text-muted fst-italic">{t('xliff-preview:empty')}</span>}</td>
            </tr>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection type="sourceChanged" title={t('xliff-preview:source-changed-title')} count={summary.sourceChanged} defaultOpen>
        <div className="mb-2 d-flex align-items-center gap-2">
          <input type="checkbox" id="skipSourceChanged" checked={skipSourceChanged} onChange={(e) => setSkipSourceChanged(e.target.checked)} />
          <label htmlFor="skipSourceChanged" style={{ fontSize: '0.85rem' }}>
            {t('xliff-preview:skip-source-changed')}
          </label>
        </div>
        <GroupedPreviewTables
          groups={sourceChanged}
          columns={[t('xliff-preview:xliff-source'), t('xliff-preview:current-value'), t('xliff-preview:target')]}
          renderRow={(item) => (
            <tr key={item.key}>
              <td className="text-decoration-line-through">{item.xliffSource}</td>
              <td className="fw-semibold">{item.currentValue}</td>
              <td>{item.target || <span className="text-muted fst-italic">{t('xliff-preview:empty')}</span>}</td>
            </tr>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection type="outOfBounds" title={t('xliff-preview:out-of-bounds-title')} count={summary.outOfBounds} defaultOpen>
        <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
          {t('xliff-preview:out-of-bounds-description')}
        </p>
        <GroupedPreviewTables
          groups={outOfBounds}
          columns={[t('xliff-preview:source'), t('xliff-preview:position')]}
          renderRow={(item) => (
            <tr key={item.key}>
              <td>{item.source}</td>
              <td className="text-muted">
                {t('xliff-preview:row')}: {item.row + 1}, {t('xliff-preview:column')}: {item.col + 1}
              </td>
            </tr>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection type="localeMismatch" title={t('xliff-preview:locale-mismatch-title')} count={summary.localeMismatch ?? 0} defaultOpen>
        <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
          {t('xliff-preview:locale-mismatch-description')} ({sourceLanguage})
        </p>
        <GroupedPreviewTables
          groups={localeMismatch ?? []}
          columns={[t('xliff-preview:source'), t('xliff-preview:target'), t('xliff-preview:table-locale')]}
          renderRow={(item) => (
            <tr key={item.key}>
              <td>{item.source}</td>
              <td>{item.target || <span className="text-muted fst-italic">{t('xliff-preview:empty')}</span>}</td>
              <td className="text-muted">{item.tableLocale}</td>
            </tr>
          )}
        />
      </CollapsibleSection>

      <CollapsibleSection type="missingTables" title={t('xliff-preview:missing-tables-title')} count={summary.missingTables} defaultOpen>
        <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
          {t('xliff-preview:missing-tables-description')}
        </p>
        <ul className="mb-0">
          {missingTables.map((table) => (
            <li key={table.superId}>
              <strong>{table.name || table.superId.slice(0, 8)}</strong> ({table.count} {t('xliff-preview:translations', { count: table.count ?? 0 })})
            </li>
          ))}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection type="newTables" title={t('xliff-preview:new-tables-title')} count={summary.newTables}>
        <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
          {t('xliff-preview:new-tables-description')}
        </p>
        <ul className="mb-0">
          {newTables.map((t) => (
            <li key={t.superId}>
              <strong>{t.name || t.superId.slice(0, 8)}</strong>
            </li>
          ))}
        </ul>
      </CollapsibleSection>
    </Modal>
  );
}
